import React from 'react';
import { Formik, Form, Field, FieldArray, getIn } from 'formik';
import * as Yup from 'yup';
import css from './style.module.css';
import { ROLE_OPTIONS } from './hqApi';

const MAX_ROWS = 50;

const emptyRow = { name: '', email: '', userRole: 'baseUser', active: true };

const validationSchema = Yup.object({
    users: Yup.array()
        .of(
            Yup.object({
                name: Yup.string().trim().required('Name is required'),
                email: Yup.string().trim().email('Invalid email').required('Email is required'),
                userRole: Yup.string()
                    .oneOf(ROLE_OPTIONS.map((r) => r.value), 'Invalid role')
                    .required('Role is required'),
                active: Yup.boolean(),
            })
        )
        .min(1)
        .max(MAX_ROWS),
});

function duplicateEmails(users) {
    const counts = {};
    users.forEach((u) => {
        const email = (u.email || '').trim().toLowerCase();
        if (email) counts[email] = (counts[email] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter((email) => counts[email] > 1));
}

export default function AddUsersForm({ org, initialRows, onSubmit, isSubmitting, submitError }) {
    const initialValues = {
        users: initialRows?.length ? initialRows : [{ ...emptyRow }],
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize={true}
            onSubmit={(values) => {
                if (duplicateEmails(values.users).size > 0) return;
                onSubmit(values.users);
            }}
        >
            {({ values, errors, touched }) => {
                const dups = duplicateEmails(values.users);
                const fieldError = (index, field) => {
                    const error = getIn(errors, `users[${index}].${field}`);
                    const isTouched = getIn(touched, `users[${index}].${field}`);
                    return error && isTouched ? error : null;
                };

                return (
                    <Form noValidate>
                        <FieldArray name="users">
                            {({ push, remove }) => (
                                <>
                                    <div className={`${css.rowsGrid} d-none d-md-grid`}>
                                        <div className={css.rowsHeaderCell}>Name</div>
                                        <div className={css.rowsHeaderCell}>Email</div>
                                        <div className={css.rowsHeaderCell}>Role</div>
                                        <div className={css.rowsHeaderCell}>Active</div>
                                        <div className={css.rowsHeaderCell}></div>
                                    </div>

                                    {values.users.map((row, index) => {
                                        const email = (row.email || '').trim().toLowerCase();
                                        const isDup = email && dups.has(email);
                                        return (
                                            <div className={`${css.rowsGrid} mb-2`} key={index}>
                                                <div>
                                                    <Field
                                                        name={`users[${index}].name`}
                                                        type="text"
                                                        placeholder="Full name"
                                                        className={`form-control form-control-sm ${fieldError(index, 'name') ? 'is-invalid' : ''}`}
                                                    />
                                                    {fieldError(index, 'name') && (
                                                        <span className={css.rowError}>{fieldError(index, 'name')}</span>
                                                    )}
                                                </div>
                                                <div>
                                                    <Field
                                                        name={`users[${index}].email`}
                                                        type="email"
                                                        placeholder="name@company.com"
                                                        className={`form-control form-control-sm ${fieldError(index, 'email') || isDup ? 'is-invalid' : ''}`}
                                                    />
                                                    {fieldError(index, 'email') && (
                                                        <span className={css.rowError}>{fieldError(index, 'email')}</span>
                                                    )}
                                                    {isDup && (
                                                        <span className={css.rowError}>Duplicate email in this batch</span>
                                                    )}
                                                </div>
                                                <div>
                                                    <Field
                                                        as="select"
                                                        name={`users[${index}].userRole`}
                                                        className={`form-select form-select-sm ${fieldError(index, 'userRole') ? 'is-invalid' : ''}`}
                                                    >
                                                        {ROLE_OPTIONS.map((role) => (
                                                            <option key={role.value} value={role.value}>
                                                                {role.label}
                                                            </option>
                                                        ))}
                                                    </Field>
                                                    {fieldError(index, 'userRole') && (
                                                        <span className={css.rowError}>{fieldError(index, 'userRole')}</span>
                                                    )}
                                                </div>
                                                <div className="form-check form-switch pt-1 ms-2">
                                                    <Field
                                                        type="checkbox"
                                                        name={`users[${index}].active`}
                                                        className="form-check-input"
                                                        title={row.active ? 'Active' : 'Inactive'}
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    className={css.removeRowBtn}
                                                    onClick={() => remove(index)}
                                                    disabled={values.users.length === 1}
                                                    aria-label={`Remove row ${index + 1}`}
                                                >
                                                    <i className="fa-solid fa-trash-can" aria-hidden="true"></i>
                                                </button>
                                            </div>
                                        );
                                    })}

                                    <div className="d-flex align-items-center gap-3 mt-3">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => push({ ...emptyRow })}
                                            disabled={values.users.length >= MAX_ROWS}
                                        >
                                            <i className="fa-solid fa-plus me-1" aria-hidden="true"></i>
                                            Add row
                                        </button>
                                        {values.users.length >= MAX_ROWS && (
                                            <span className={css.maxRowsHint}>Max {MAX_ROWS} per batch</span>
                                        )}
                                    </div>
                                </>
                            )}
                        </FieldArray>

                        {submitError && (
                            <div className="alert alert-danger mt-3 mb-0" role="alert">
                                {submitError}
                            </div>
                        )}

                        <div className="mt-4">
                            <button
                                type="submit"
                                className={`btn ${css.btnPrimary}`}
                                disabled={isSubmitting || dups.size > 0}
                            >
                                {isSubmitting ? 'Creating users…' : `Create ${values.users.length} user${values.users.length === 1 ? '' : 's'} in ${org.companyName}`}
                                {isSubmitting && (
                                    <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                                )}
                            </button>
                            <p className={css.tempPasswordNote}>
                                Each user receives a temporary password and will be asked to set a new one on first sign-in.
                            </p>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
}
